import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, Typography, TextField, Button, Paper, Grid, Switch, 
  FormControlLabel, Tab, Tabs, CircularProgress, Chip, Divider,
  Accordion, AccordionSummary, AccordionDetails, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select,
  FormControl, InputLabel, TableContainer, Table, TableHead, TableRow, 
  TableCell, TableBody, ListSubheader, IconButton, Collapse, Card, CardContent
} from '@mui/material';
import {
  Save, ArrowBack, PlayArrow, ExpandMore, Refresh, Delete,
  ContentCopy, FlashOn, Add, Edit, ExpandLess, Visibility,
  Code, Science, CompareArrows, Assessment, NavigateBefore, NavigateNext,
  AutoFixHigh
} from '@mui/icons-material';
import { AdminService } from '../../services/adminService';
import { 
  GAME_TYPES, ROUTES, getLessons, getThemes, DIFFICULTIES, getGameConfig,
  getAllCompetences, getCompetencesforLevel, getGameCompetences
} from '../../config/shared_config';
import JSONEditor from '../../components/admin/JSONEditor';
import ExerciseContentVisualEditor from '../../components/admin/ExerciseContentVisualEditor';
import ExerciseParametersSummaryForm from '../../components/admin/ExerciseParametersSummaryForm';
import ExercisePreview from '../../components/admin/ExercisePreview';
import ExerciseValidationPanel from '../../components/admin/ExerciseValidationPanel';
import LoadingScreen from '../../components/LoadingScreen';
import AdminLayout from '../../layouts/AdminLayout';
import {
  FILTRES_PAR_DEFAUT,
  lireFiltresListe,
  ecrireFiltresListe,
  parametresApiListe,
} from '../../utils/exerciseListFilters';

const VALIDATION_STATUS = {
  NOUVEAU: 'nouveau',
  EN_ATTENTE: 'en_attente',
  VALIDE: 'valide',
};

const VALIDATION_STATUS_LABELS = {
  [VALIDATION_STATUS.NOUVEAU]: 'Nouveau',
  [VALIDATION_STATUS.EN_ATTENTE]: 'En attente de validation',
  [VALIDATION_STATUS.VALIDE]: 'Validé',
};

const VALIDATION_STATUS_COLORS = {
  [VALIDATION_STATUS.NOUVEAU]: 'default',
  [VALIDATION_STATUS.EN_ATTENTE]: 'warning',
  [VALIDATION_STATUS.VALIDE]: 'success',
};
// Composant principal
const ExerciseDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewMode = location.pathname.includes('/view');
  const isNewMode = !id || id === 'new';

  const [exercise, setExercise] = useState({
    id: null,
    title: '',
    description: '',
    type: 'manual',
    gameType: '',
    parameters: {},
    content: {},
    isActive: true,
    isFlagged: false,
    validationStatus: VALIDATION_STATUS.NOUVEAU,
    source: 'admin'
  });
  
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Rapport du gate de validation (génération + correction) à afficher à l'admin.
  const [contentValidation, setContentValidation] = useState(null);
  // Niveau estimé (cycle × difficulté) renvoyé par la correction d'un exercice complet.
  const [estimatedLevel, setEstimatedLevel] = useState(null);
  const [activeTab, setActiveTab] = useState(2); // Commencer directement sur l'onglet Contenu
  const [usageStats, setUsageStats] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [generateDialog, setGenerateDialog] = useState(false);
  
  // Navigation précédent / suivant dans la liste telle que l'écran de liste la
  // montre (mêmes filtres, même tri) : le serveur calcule les voisins.
  const [voisins, setVoisins] = useState(null);

  /** Sauvegarde automatique (édition uniquement) */
  const [autoSaveState, setAutoSaveState] = useState('idle'); // idle | saving | saved | error
  const lastSavedRef = useRef('');
  const autoSaveTimerRef = useRef(null);
  const exerciseRef = useRef(exercise);
  useEffect(() => {
    exerciseRef.current = exercise;
  }, [exercise]);

  const buildAutoSavePayload = useCallback((ex) => ({
    parameters: ex.parameters,
    content: ex.content,
    title: ex.title,
    description: ex.description,
    isActive: ex.isActive,
    isFlagged: ex.isFlagged,
    validationStatus: ex.validationStatus || VALIDATION_STATUS.NOUVEAU,
    // La notion est une colonne (`notion_id`) : le serveur la change et aligne
    // `parameters.competence`.
    notionId: ex.notionId ?? null,
  }), []);

  const serializeAutoSavePayload = useCallback(
    (ex) => JSON.stringify(buildAutoSavePayload(ex)),
    [buildAutoSavePayload]
  );
  
  // États pour la gestion des prompts
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showPromptExamples, setShowPromptExamples] = useState(false);
  const [newPrompt, setNewPrompt] = useState({
    name: '',
    description: '',
    promptContent: ''
  });
  const [formatInstructions, setFormatInstructions] = useState('');
  const [loadingFormatInstructions, setLoadingFormatInstructions] = useState(false);
  
  const [parametersForm, setParametersForm] = useState({
    schoolLevel: 'CE2',
    difficulty: 'medium',
    theme: '',
    competence: null,
    gameType: 'speed_game',
    totalWords: 10,         // Paramètre pour speed_game, word_lists et dictée
    numOptions: 4,          // Nombre d'options pour le speed_game
    mode: 'text',           // Pour la dictée: 'text' ou 'word_lists'
    // Nouveaux paramètres pour les prompts
    useCustomPrompt: false,
    selectedPromptId: '',
    customPrompt: '',
    correctionInteractionMode: 'correct_errors',
    correctionTargetRole: '',
  });

  // Récupérer les compétences et thèmes selon le niveau
  const { themes = {}, lessons = {} } = getGameCompetences(parametersForm.schoolLevel) || {};
  const allCompetences = getAllCompetences() || [];

  // Paramètres spécifiques par type de jeu
  const gameSpecificParams = {
    'speed_game': ['totalWords', 'numOptions'],
    'text_holes': ['totalWords'],
    'correction_game': [],
    'word_lists': ['totalWords'],
    'dictation': ['totalWords', 'mode'],
    'association': ['totalWords'],
    'categorisation': ['totalWords'],
    'speed_sort': ['totalWords']
  };

  // Fonction pour vérifier si un paramètre est applicable au type de jeu actuel
  const isParamApplicable = (param) => {
    return gameSpecificParams[parametersForm.gameType]?.includes(param);
  };

  // Cette fonction transforme l'identifiant d'une compétence en structure complète
  // attendue par le backend
  const formatCompetenceForBackend = (competenceId) => {
    if (!competenceId) return null;
    
    // Chercher la compétence sélectionnée dans la liste complète des compétences
    const selectedCompetence = allCompetences.find(comp => 
      `${comp.domain}-${comp.competence}-${comp.notion}` === competenceId
    );
    
    if (!selectedCompetence) return null;
    
    return {
      domain: selectedCompetence.domain,
      competence: selectedCompetence.competence,
      notion: selectedCompetence.notion
    };
  };

  // Exemples de prompts par type de jeu
  const getPromptExamples = (gameType) => {
    const examples = {
      'speed_game': [
        {
          name: "Conjugaison au futur",
          prompt: `Génère {{totalWords}} mots de conjugaison au futur pour le niveau {{schoolLevel}}.
Concentre-toi sur les verbes du 1er groupe et les verbes être/avoir.
Chaque mot doit avoir {{numOptions}} options avec des erreurs typiques de conjugaison.
Difficulté: {{difficulty}}`
        },
        {
          name: "Homonymes courants",
          prompt: `Crée {{totalWords}} exercices sur les homonymes pour {{schoolLevel}}.
Utilise des paires comme: vert/verre, mer/mère, son/sont, etc.
Inclus des phrases d'usage pour contextualiser chaque mot.
Niveau de difficulté: {{difficulty}}`
        }
      ],
      'text_holes': [
        {
          name: "Texte narratif avec accords",
          prompt: `Écris un petit texte narratif de 4-5 phrases pour {{schoolLevel}}.
Thème: {{theme}}
Crée {{totalWords}} trous focalisés sur les accords (sujet-verbe, adjectifs).
Le texte doit être engageant et adapté au niveau {{difficulty}}.`
        },
        {
          name: "Description avec vocabulaire",
          prompt: `Génère un texte descriptif sur le thème {{theme}} pour {{schoolLevel}}.
Inclus du vocabulaire spécialisé et crée des trous sur les mots-clés.
Difficulté {{difficulty}} avec {{totalWords}} mots à deviner.`
        }
      ],
      'correction_game': [
        {
          name: "Erreurs d'orthographe courantes",
          prompt: `Crée un texte de 3-4 phrases avec des erreurs d'orthographe typiques du niveau {{schoolLevel}}.
Inclus des erreurs sur: les doubles consonnes, les accents, les terminaisons.
Thème: {{theme}}
Difficulté: {{difficulty}}`
        },
        {
          name: "Erreurs grammaticales",
          prompt: `Génère un texte avec des erreurs de grammaire pour {{schoolLevel}}.
Focus sur: accords sujet-verbe, accords dans le groupe nominal, homophones.
Chaque erreur doit avoir une explication pédagogique claire.
Niveau: {{difficulty}}`
        }
      ],
      'word_lists': [
        {
          name: "Vocabulaire thématique",
          prompt: `Crée une liste de {{totalWords}} mots sur le thème {{theme}}.
Niveau {{schoolLevel}}, difficulté {{difficulty}}.
Inclus des mots variés: noms, verbes, adjectifs liés au thème.
Évite les mots trop complexes pour le niveau.`
        }
      ],
      'dictation': [
        {
          name: "Dictée narrative",
          prompt: `Écris une dictée de {{totalWords}} mots environ pour {{schoolLevel}}.
Crée une petite histoire sur le thème {{theme}}.
Inclus des difficultés orthographiques adaptées au niveau.
Difficulté: {{difficulty}}`
        }
      ]
    };
    
    return examples[gameType] || [];
  };

  // ✨ NOUVEAUX ÉTATS POUR LE PREVIEW ET TEST DE PROMPTS
  const [promptPreview, setPromptPreview] = useState({
    content: '',
    variables: {},
    loading: false,
    error: null
  });
  const [testResults, setTestResults] = useState({
    content: null,
    loading: false,
    error: null,
    metadata: null
  });
  const [promptComparison, setPromptComparison] = useState({
    variants: [],
    results: [],
    loading: false
  });
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [currentPromptVersion, setCurrentPromptVersion] = useState('');
  const [promptHistory, setPromptHistory] = useState([]);

  // États pour la correction LLM
  const [llmCorrectionDialog, setLlmCorrectionDialog] = useState({
    open: false,
    loading: false,
    rawResponse: '',
    editedContent: '',
    parseError: null,
    batchInfo: null,
    issues: [],
  });
  const [correctingItemKey, setCorrectingItemKey] = useState(null);
  


  useEffect(() => {
    if (!isNewMode && id) {
      loadExercise();
    }
  }, [id, isNewMode]);

  const loadVoisins = useCallback(async () => {
    if (isNewMode || !id) return;
    try {
      setVoisins(await AdminService.getExerciseNeighbours(id, parametresApiListe(lireFiltresListe())));
    } catch (error) {
      console.error('Erreur lors du calcul des voisins de l\'exercice:', error);
      setVoisins(null);
    }
  }, [id, isNewMode]);

  useEffect(() => {
    loadVoisins();
  }, [loadVoisins]);

  const loadExercise = async () => {
    setLoading(true);
    try {
      const data = await AdminService.getExerciseById(id);
      const ex = data.exercise;
      lastSavedRef.current = serializeAutoSavePayload(ex);
      setExercise(ex);
      exerciseRef.current = ex;
      // Le sélecteur « Compétence ciblée » appartient au formulaire de GÉNÉRATION :
      // son état n'était jamais alimenté par l'exercice ouvert, si bien qu'il
      // affichait « Aucune » quoi qu'on relise. On l'initialise depuis les ID que
      // l'API renvoie désormais (domainId / competenceId / notionId), au format
      // de valeur qu'attend la liste.
      if (ex && ex.notionId && ex.competenceId && ex.domainId) {
        setParametersForm((prev) => ({
          ...prev,
          competence: `${ex.domainId}-${ex.competenceId}-${ex.notionId}`,
          gameType: ex.gameType || prev.gameType,
          schoolLevel: (ex.parameters && ex.parameters.schoolLevel) || prev.schoolLevel,
          difficulty: (ex.parameters && ex.parameters.difficulty) || prev.difficulty,
        }));
      }
      setUsageStats({ ...(data.stats || {}), recentUsage: data.recentUsage || [] });
      setAutoSaveState('idle');
    } catch (error) {
      console.error('Erreur lors du chargement de l\'exercice:', error);
      setErrorMessage('Erreur lors du chargement de l\'exercice');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isNewMode || !exercise.id || isViewMode) return;
    const serialized = serializeAutoSavePayload(exercise);
    if (serialized === lastSavedRef.current) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const latest = exerciseRef.current;
      const payload = buildAutoSavePayload(latest);
      const payloadStr = JSON.stringify(payload);
      if (payloadStr === lastSavedRef.current) {
        setAutoSaveState('idle');
        return;
      }
      setAutoSaveState('saving');
      setErrorMessage('');
      try {
        const updated = await AdminService.updateExercise(latest.id, payload);
        lastSavedRef.current = payloadStr;
        if (updated?.validationStatus && updated.validationStatus !== latest.validationStatus) {
          setExercise((prev) => ({ ...prev, validationStatus: updated.validationStatus }));
        }
        appliquerNotionServeur(updated);
        setAutoSaveState('saved');
        setTimeout(() => {
          setAutoSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
        }, 2200);
      } catch (error) {
        console.error('Erreur lors de la sauvegarde automatique:', error);
        setAutoSaveState('error');
        setErrorMessage('Erreur lors de la sauvegarde automatique');
      }
    }, 650);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [exercise, isNewMode, isViewMode, buildAutoSavePayload, serializeAutoSavePayload]);

  const flushAutoSave = async () => {
    if (isNewMode || !exercise.id || isViewMode) return;
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    const latest = exerciseRef.current;
    const payload = buildAutoSavePayload(latest);
    const payloadStr = JSON.stringify(payload);
    if (payloadStr === lastSavedRef.current) return;
    setAutoSaveState('saving');
    setErrorMessage('');
    try {
      const updated = await AdminService.updateExercise(latest.id, payload);
      lastSavedRef.current = payloadStr;
      if (updated?.validationStatus && updated.validationStatus !== latest.validationStatus) {
        setExercise((prev) => ({ ...prev, validationStatus: updated.validationStatus }));
      }
      appliquerNotionServeur(updated);
      setAutoSaveState('saved');
    } catch (error) {
      console.error('Erreur flush sauvegarde:', error);
      setAutoSaveState('error');
      setErrorMessage('Erreur lors de la sauvegarde');
      throw error;
    }
  };

  // Navigation vers l'exercice précédent / suivant de la liste
  const allerA = async (cible) => {
    if (!cible) return;
    try {
      await flushAutoSave();
    } catch {
      return;
    }
    navigate(`/admin/exercises/edit/${cible}`);
  };
  const goToPreviousExercise = () => allerA(voisins?.precedent);
  const goToNextExercise = () => allerA(voisins?.suivant);

  // Naviguer dans toute la base (filtres de la liste remis à zéro)
  const resetFiltersAndReload = async () => {
    ecrireFiltresListe({ ...FILTRES_PAR_DEFAUT });
    await loadVoisins();
  };

  const handleChange = (field, value) => {
    setExercise(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Notion relue au serveur après un changement : ses libellés pour l'en-tête et
   * `parameters` que le serveur a alignés. La sérialisation de référence suit, pour
   * que la sauvegarde automatique ne renvoie pas ce qu'elle vient d'écrire.
   */
  const appliquerNotionServeur = (updated) => {
    if (!updated || updated.notionLabel === undefined) return;
    setExercise((prev) => {
      const next = {
        ...prev,
        notionId: updated.notionId,
        notionLabel: updated.notionLabel,
        competenceId: updated.competenceId,
        competenceLabel: updated.competenceLabel,
        domainId: updated.domainId,
        domainLabel: updated.domainLabel,
        parameters: updated.parameters ?? prev.parameters,
      };
      lastSavedRef.current = serializeAutoSavePayload(next);
      return next;
    });
  };

  const handleParametersChange = (field, value) => {
    setParametersForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Si on change le type de jeu, réinitialiser certains paramètres
      if (field === 'gameType') {
        updated.theme = '';
        updated.competence = '';
        updated.selectedPromptId = '';
        updated.customPrompt = '';
        updated.useCustomPrompt = false;
        
        // Charger les prompts disponibles pour ce type de jeu
        loadAvailablePrompts(value);
        
        // Définir des valeurs par défaut selon le type de jeu
        switch (value) {
          case 'speed_game':
            updated.totalWords = 10;
            updated.numOptions = 4;
            break;
          case 'text_holes':
            updated.totalWords = 6; // Nombre de trous
            break;
          case 'word_lists':
            updated.totalWords = 10; // Nombre de mots
            break;
          case 'dictation':
            updated.totalWords = 20; // Longueur approximative
            updated.mode = 'text';
            break;
          case 'association':
          case 'categorisation':
          case 'speed_sort':
            updated.totalWords = 6; // Nombre d'items
            break;
          default:
            break;
        }
      }
      
      return updated;
    });
  };

  // Charger les prompts disponibles pour un type de jeu
  const loadAvailablePrompts = async (gameType) => {
    if (!gameType) return;
    
    try {
      const response  = await AdminService.getActivePromptsByGameType(gameType);
      setAvailablePrompts(response.prompts);
    } catch (error) {
      console.error('Erreur lors du chargement des prompts:', error);
    }
  };

  // Charger les instructions de format pour un type de jeu
  const loadFormatInstructions = async (gameType) => {
    if (!gameType) return;
    
    setLoadingFormatInstructions(true);
    try {
      const response = await AdminService.getFormatInstructions(gameType);
      setFormatInstructions(response.formatInstructions);
    } catch (error) {
      console.error('Erreur lors du chargement des instructions de format:', error);
      setFormatInstructions('');
    } finally {
      setLoadingFormatInstructions(false);
    }
  };

  // Charger les prompts lors du changement de gameType dans le dialog
  useEffect(() => {
    if (generateDialog && parametersForm.gameType) {
      loadAvailablePrompts(parametersForm.gameType);
    }
  }, [generateDialog, parametersForm.gameType]);

  // Effet pour charger les instructions de format lors de l'ouverture du dialog de prompt
  useEffect(() => {
    if (showPromptDialog && parametersForm.gameType) {
      loadFormatInstructions(parametersForm.gameType);
    }
  }, [showPromptDialog, parametersForm.gameType]);

  const handleContentChange = (content) => {
    try {
      setExercise(prev => ({ ...prev, content }));
      setContentValidation(null); // le contenu a changé → l'ancien rapport n'est plus valable
      setEstimatedLevel(null);
    } catch (error) {
      console.error('Erreur de parsing JSON:', error);
    }
  };

  // Convertit les "issues" de correction (déterministe + LLM) en rapport pour le panneau.
  const issuesToValidation = (issues = [], { proposed = false } = {}) => ({
    isValid: issues.length === 0,
    proposed,
    defects: issues.map((it) => ({
      kind: it.source === 'deterministic'
        ? (it.type === 'coherence' ? 'alignment' : it.type === 'shortcut' ? 'shortcut' : 'structure')
        : 'semantic',
      type: it.type,
      severity: it.severity,
      description: it.description,
      itemIndex: it.itemIndex,
    })),
  });

  const handleParametersJsonChange = (parameters) => {
    try {
      setExercise(prev => ({ ...prev, parameters }));
    } catch (error) {
      console.error('Erreur de parsing JSON:', error);
    }
  };

  const handleSave = async () => {
    if (!isNewMode) return;
    setSaving(true);
    setErrorMessage('');
    
    try {
      const result = await AdminService.createExercise(exercise);
      navigate(ROUTES.ADMIN.EXERCISES.EDIT(result.id));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setErrorMessage('Erreur lors de la sauvegarde de l\'exercice');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setErrorMessage('');
    
    try {
      // Formater la compétence au format attendu par le backend si sélectionnée
      const competenceObj = formatCompetenceForBackend(parametersForm.competence);
      
      // Préparer les paramètres communs
      const baseParams = {
        schoolLevel: parametersForm.schoolLevel,
        difficulty: parametersForm.difficulty
      };
      
      // Sélectionner les paramètres spécifiques au jeu
      const gameParams = {};
      
      if (competenceObj) {
        gameParams.competence = competenceObj;
      } else if (parametersForm.theme) {
        gameParams.theme = parametersForm.theme;
      }
      
      // Ajouter les paramètres spécifiques selon le type de jeu
      if (isParamApplicable('totalWords')) {
        gameParams.totalWords = parseInt(parametersForm.totalWords) || 10;
      }
      
      if (isParamApplicable('numOptions') && parametersForm.gameType === 'speed_game') {
        gameParams.numOptions = parseInt(parametersForm.numOptions) || 4;
      }
      
      if (parametersForm.gameType === 'dictation') {
        gameParams.mode = parametersForm.mode;
      }

      if (parametersForm.gameType === 'correction_game'
        && parametersForm.correctionInteractionMode === 'identify') {
        gameParams.interactionMode = 'identify';
        if (parametersForm.correctionTargetRole?.trim()) {
          gameParams.targetRole = parametersForm.correctionTargetRole.trim();
        }
      }
      
      // Construire l'objet final pour la génération
      const generationParams = {
        gameType: parametersForm.gameType,
        parameters: {
          ...baseParams,
          ...gameParams
        }
      };
      
      // Ajouter les paramètres de prompt si nécessaire
      if (parametersForm.useCustomPrompt) {
        if (parametersForm.selectedPromptId) {
          generationParams.customPromptId = parametersForm.selectedPromptId;
        } else if (parametersForm.customPrompt) {
          generationParams.customPrompt = parametersForm.customPrompt;
        }
      }
      
      console.log('Paramètres de génération:', generationParams);
      
      const result = await AdminService.generateExercise(generationParams);
      
      if (result.generatedContent) {
        setExercise(prev => ({
          ...prev,
          gameType: parametersForm.gameType, // Mettre à jour le type de jeu
          content: result.generatedContent,
          parameters: result.parameters || generationParams.parameters,
        }));
        setContentValidation(result.validation || null); // rapport du gate (déterministe + sémantique)
        setGenerateDialog(false);
      }
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      setErrorMessage(error.message || 'Erreur lors de la génération de l\'exercice');
    } finally {
      setGenerating(false);
    }
  };

  const refreshStats = async () => {
    if (!isNewMode) {
      try {
        const stats = await AdminService.getExerciseUsageStats(id);
        setUsageStats(stats);
      } catch (error) {
        console.error('Erreur lors du rafraîchissement des statistiques:', error);
      }
    }
  };

  const handleCorrectItemWithLLM = async (arrayKey, index) => {
    const itemKey = `${arrayKey}-${index}`;
    setCorrectingItemKey(itemKey);
    setErrorMessage('');
    try {
      const correctedItem = await AdminService.correctExerciseItemWithLLM({
        gameType: exercise.gameType,
        content: exercise.content,
        parameters: exercise.parameters,
        arrayKey,
        itemIndex: index,
      });
      const items = [...(exercise.content?.[arrayKey] || [])];
      items[index] = correctedItem;
      handleContentChange({ ...exercise.content, [arrayKey]: items });
      // Correction LLM (sous-exercice) → relecture + signalement manuel.
      setExercise(prev => ({ ...prev, isFlagged: true, validationStatus: VALIDATION_STATUS.EN_ATTENTE }));
    } catch (error) {
      console.error('Erreur lors de la correction LLM de l\'élément:', error);
      setErrorMessage(error.message || 'Erreur lors de la correction de l\'élément');
    } finally {
      setCorrectingItemKey(null);
    }
  };

  const handleCorrectWithLLM = async () => {
    setLlmCorrectionDialog({ open: true, loading: true, rawResponse: '', editedContent: '', parseError: null, batchInfo: null, issues: [] });
    try {
      const result = await AdminService.correctExerciseWithLLM({
        gameType: exercise.gameType,
        content: exercise.content,
        parameters: exercise.parameters,
      });
      const raw = result.response || '';
      const issues = Array.isArray(result.issues) ? result.issues : [];
      setContentValidation(issuesToValidation(issues, { proposed: result.semanticProposed })); // résumé du gate (déterministe + sémantique) dans le panneau
      setEstimatedLevel(result.estimatedLevel || null); // niveau estimé (exercice complet uniquement)
      const batchInfo = result.batched ? { chunkCount: result.chunkCount } : null;
      // Try to parse the JSON to pre-fill the editor with formatted content
      let editedContent = raw;
      let parseError = null;
      try {
        // Normalize leading/trailing whitespace, then strip markdown code fences (```json ... ``` or ``` ... ```)
        const trimmedRaw = raw.trim();
        const stripped = trimmedRaw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').trim();
        const parsed = JSON.parse(stripped);
        editedContent = JSON.stringify(parsed, null, 2);
      } catch {
        // Fallback: extract JSON object from surrounding text
        const match = raw.match(/\{[\s\S]*\}/);
        if (match) {
          try {
            const parsed = JSON.parse(match[0]);
            editedContent = JSON.stringify(parsed, null, 2);
          } catch {
            parseError = 'La réponse LLM ne semble pas être du JSON valide. Vous pouvez la corriger manuellement.';
            editedContent = raw;
          }
        } else {
          parseError = 'La réponse LLM ne semble pas être du JSON valide. Vous pouvez la corriger manuellement.';
          editedContent = raw;
        }
      }
      setLlmCorrectionDialog({ open: true, loading: false, rawResponse: raw, editedContent, parseError, batchInfo, issues });
    } catch (error) {
      console.error('Erreur lors de la correction LLM:', error);
      setLlmCorrectionDialog({ open: true, loading: false, rawResponse: '', editedContent: '', parseError: error.message || 'Erreur lors de la communication avec le LLM', batchInfo: null, issues: [] });
    }
  };

  const handleApplyLLMCorrection = () => {
    try {
      const parsed = JSON.parse(llmCorrectionDialog.editedContent);
      // Les corrections LLM sont souvent imparfaites → l'exercice corrigé passe
      // en relecture (en_attente) et est SIGNALÉ pour une revue manuelle.
      setExercise(prev => ({
        ...prev,
        content: parsed,
        isFlagged: true,
        validationStatus: VALIDATION_STATUS.EN_ATTENTE,
      }));
      setContentValidation(null);
      setEstimatedLevel(null);
      setLlmCorrectionDialog({ open: false, loading: false, rawResponse: '', editedContent: '', parseError: null });
    } catch {
      setLlmCorrectionDialog(prev => ({ ...prev, parseError: 'JSON invalide — corrigez le contenu avant d\'appliquer.' }));
    }
  };

  // ✨ NOUVELLES FONCTIONS POUR LE PREVIEW ET TEST DE PROMPTS

  // Prévisualiser le prompt qui sera utilisé
  const previewPrompt = async () => {
    setPromptPreview(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Formater les paramètres avec la compétence correctement structurée
      const formattedParameters = {
        ...parametersForm,
        competence: formatCompetenceForBackend(parametersForm.competence)
      };
      
      const response = await AdminService.previewPrompt({
        gameType: parametersForm.gameType,
        parameters: formattedParameters,
        customPrompt: parametersForm.useCustomPrompt ? parametersForm.customPrompt : null,
        selectedPromptId: parametersForm.selectedPromptId || null
      });
      
      setPromptPreview({
        content: response.prompt,
        variables: response.variables || {},
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Erreur lors du preview du prompt:', error);
      setPromptPreview(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la génération du preview'
      }));
    }
  };

  // Tester un prompt et voir les résultats
  const testPrompt = async (customPromptContent = null) => {
    setTestResults(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Formater les paramètres avec la compétence correctement structurée
      const formattedParameters = {
        ...parametersForm,
        competence: formatCompetenceForBackend(parametersForm.competence)
      };
      
      const response = await AdminService.testPrompt({
        gameType: parametersForm.gameType,
        parameters: formattedParameters,
        customPrompt: customPromptContent || parametersForm.customPrompt,
        selectedPromptId: parametersForm.selectedPromptId || null
      });
      
      setTestResults({
        content: response.generatedContent,
        loading: false,
        error: null,
        metadata: {
          generationTime: response.generationTime,
          tokensUsed: response.tokensUsed,
          qualityScore: response.qualityScore
        }
      });
    } catch (error) {
      console.error('Erreur lors du test du prompt:', error);
      setTestResults(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors du test du prompt'
      }));
    }
  };

  // Comparer plusieurs versions de prompts
  const comparePrompts = async (promptVariants) => {
    setPromptComparison(prev => ({ ...prev, loading: true }));
    
    try {
      // Formater les paramètres avec la compétence correctement structurée
      const formattedParameters = {
        ...parametersForm,
        competence: formatCompetenceForBackend(parametersForm.competence)
      };
      
      const results = await Promise.all(
        promptVariants.map(async (prompt) => {
          const response = await AdminService.testPrompt({
            gameType: parametersForm.gameType,
            parameters: formattedParameters,
            customPrompt: prompt.content
          });
          return {
            prompt: prompt,
            result: response.generatedContent,
            metadata: response.metadata
          };
        })
      );
      
      setPromptComparison({
        variants: promptVariants,
        results: results,
        loading: false
      });
    } catch (error) {
      console.error('Erreur lors de la comparaison:', error);
      setPromptComparison(prev => ({ ...prev, loading: false }));
    }
  };

  // Sauvegarder une version de prompt avec historique
  const savePromptVersion = async (promptContent, name, description) => {
    try {
      // Formater les paramètres avec la compétence correctement structurée
      const formattedParameters = {
        ...parametersForm,
        competence: formatCompetenceForBackend(parametersForm.competence)
      };
      
      const response = await AdminService.savePromptVersion({
        gameType: parametersForm.gameType,
        name: name,
        description: description,
        promptContent: promptContent,
        parameters: formattedParameters,
        parentPromptId: parametersForm.selectedPromptId || null
      });
      
      // Recharger la liste des prompts
      await loadAvailablePrompts(parametersForm.gameType);
      
      // Ajouter à l'historique local
      setPromptHistory(prev => [...prev, {
        id: response.id,
        name: name,
        description: description,
        content: promptContent,
        createdAt: new Date().toISOString()
      }]);
      
      return response;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du prompt:', error);
      throw error;
    }
  };



  const renderGenerateDialog = () => {
    // Organiser les compétences par domaine pour faciliter la sélection
    const competencesByDomain = {};
    (allCompetences || []).forEach(comp => {
      if (!competencesByDomain[comp.domain]) {
        competencesByDomain[comp.domain] = [];
      }
      competencesByDomain[comp.domain].push(comp);
    });

    return (
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Générer un exercice via LLM</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Première ligne - Type d'exercice et niveau */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type d'exercice</InputLabel>
                <Select
                  value={parametersForm.gameType}
                  onChange={(e) => handleParametersChange('gameType', e.target.value)}
                  label="Type d'exercice"
                >
                  {Object.values(GAME_TYPES || {}).map((game) => (
                    <MenuItem key={game.id} value={game.id}>{game.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Niveau</InputLabel>
                <Select
                  value={parametersForm.schoolLevel}
                  onChange={(e) => handleParametersChange('schoolLevel', e.target.value)}
                  label="Niveau"
                >
                  {['CP', 'CE1', 'CE2', 'CM1', 'CM2', '6ème', '5ème', '4ème', '3ème'].map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {parametersForm.gameType === 'correction_game' && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Mode correction_game</InputLabel>
                    <Select
                      value={parametersForm.correctionInteractionMode || 'correct_errors'}
                      onChange={(e) => handleParametersChange('correctionInteractionMode', e.target.value)}
                      label="Mode correction_game"
                    >
                      <MenuItem value="correct_errors">Correction d&apos;erreurs (défaut)</MenuItem>
                      <MenuItem value="identify">Repérage grammatical (identify)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {parametersForm.correctionInteractionMode === 'identify' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Rôle cible (ex. adverbe, nom, verbe)"
                      value={parametersForm.correctionTargetRole || ''}
                      onChange={(e) => handleParametersChange('correctionTargetRole', e.target.value)}
                      placeholder="adverbe"
                      helperText="Utilisé par le prompt de génération en mode identify"
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Section des prompts personnalisés */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="h6" color="primary">
                  Prompt de génération
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={parametersForm.useCustomPrompt}
                    onChange={(e) => handleParametersChange('useCustomPrompt', e.target.checked)}
                  />
                }
                label="Utiliser un prompt personnalisé"
              />
            </Grid>

            {parametersForm.useCustomPrompt && (
              <>
                {/* Sélection de prompt existant */}
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel>Prompt sauvegardé</InputLabel>
                    <Select
                      value={parametersForm.selectedPromptId}
                      onChange={(e) => {
                        handleParametersChange('selectedPromptId', e.target.value);
                        if (e.target.value) {
                          handleParametersChange('customPrompt', '');
                        }
                      }}
                      label="Prompt sauvegardé"
                    >
                      <MenuItem value="">Aucun (utiliser un prompt personnalisé)</MenuItem>
                      {availablePrompts?.map((prompt) => (
                        <MenuItem key={prompt.id} value={prompt.id}>
                          {prompt.name}
                          {prompt.description && (
                            <Typography variant="body2" color="textSecondary" component="span">
                              {' - ' + prompt.description}
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setShowPromptDialog(true)}
                    sx={{ height: '56px' }}
                  >
                    Nouveau prompt
                  </Button>
                </Grid>

                {/* Zone de texte pour prompt personnalisé */}
                {!parametersForm.selectedPromptId && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label="Prompt personnalisé"
                      value={parametersForm.customPrompt}
                      onChange={(e) => handleParametersChange('customPrompt', e.target.value)}
                      placeholder="Écrivez votre prompt ici. Vous pouvez utiliser des variables comme {{schoolLevel}}, {{difficulty}}, {{theme}}, etc."
                      helperText="Utilisez {{variable}} pour insérer des paramètres dynamiques"
                    />
                  </Grid>
                )}

                {/* Bouton pour voir les variables disponibles */}
                <Grid item xs={12}>
                  <Button
                    size="small"
                    onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                    endIcon={showCustomPrompt ? <ExpandLess /> : <ExpandMore />}
                  >
                    Variables disponibles
                  </Button>
                  <Collapse in={showCustomPrompt}>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Variables disponibles dans votre prompt :</strong>
                      </Typography>
                      <Grid container spacing={1}>
                        {[
                          '{{schoolLevel}}', '{{difficulty}}', '{{theme}}', '{{competence}}',
                          '{{totalWords}}', '{{numOptions}}', '{{mode}}'
                        ].map((variable) => (
                          <Grid item key={variable}>
                            <Chip 
                              label={variable} 
                              size="small" 
                              onClick={() => {
                                const newPrompt = parametersForm.customPrompt + variable;
                                handleParametersChange('customPrompt', newPrompt);
                              }}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Collapse>
                </Grid>
              </>
            )}

            {/* Paramètres standard (affichés seulement si pas de prompt personnalisé) */}
            {!parametersForm.useCustomPrompt && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="h6" color="primary">
                      Paramètres de génération
                    </Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Difficulté</InputLabel>
                    <Select
                      value={parametersForm.difficulty}
                      onChange={(e) => handleParametersChange('difficulty', e.target.value)}
                      label="Difficulté"
                    >
                      {Object.values(DIFFICULTIES || {}).map((diff) => (
                        <MenuItem key={diff.id} value={diff.id}>{diff.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Sélection de compétence organisée par domaine */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Compétence</InputLabel>
                    <Select
                      value={parametersForm.competence || ''}
                      onChange={(e) => {
                        // Si on sélectionne une compétence, effacer le thème
                        handleParametersChange('competence', e.target.value);
                        if (e.target.value) handleParametersChange('theme', '');
                      }}
                      label="Compétence"
                    >
                      <MenuItem value="">Aucune</MenuItem>
                      {Object.entries(competencesByDomain || {}).map(([domain, comps]) => (
                        <ListSubheader key={domain}>
                          {domain.charAt(0).toUpperCase() + domain.slice(1)}
                        </ListSubheader>
                      )).concat(
                        (allCompetences || []).map(comp => (
                          <MenuItem 
                            key={`${comp.domain}-${comp.competence}-${comp.notion}`} 
                            value={`${comp.domain}-${comp.competence}-${comp.notion}`}
                          >
                            {comp.domain}: {comp.competence} - {comp.notion}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Sélection de thème (si aucune compétence n'est sélectionnée) */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Thème</InputLabel>
                    <Select
                      value={parametersForm.theme || ''}
                      onChange={(e) => {
                        // Si on sélectionne un thème, effacer la compétence
                        handleParametersChange('theme', e.target.value);
                        if (e.target.value) handleParametersChange('competence', '');
                      }}
                      label="Thème"
                      disabled={!!parametersForm.competence} // Désactiver si une compétence est sélectionnée
                    >
                      <MenuItem value="">Aucun</MenuItem>
                      {Object.values(themes || {}).map((theme) => (
                        <MenuItem key={theme.notion} value={theme.notion}>{theme.notion}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Paramètres spécifiques aux jeux */}
                {isParamApplicable('totalWords') && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={parametersForm.gameType === 'dictation' ? "Nombre de mots approximatif" : 
                             parametersForm.gameType === 'text_holes' ? "Nombre de trous" : 
                             "Nombre de mots"}
                      value={parametersForm.totalWords}
                      onChange={(e) => handleParametersChange('totalWords', e.target.value)}
                      InputProps={{ inputProps: { min: 5, max: 100 } }}
                    />
                  </Grid>
                )}
                
                {isParamApplicable('numOptions') && parametersForm.gameType === 'speed_game' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Nombre d'options par mot"
                      value={parametersForm.numOptions}
                      onChange={(e) => handleParametersChange('numOptions', e.target.value)}
                      InputProps={{ inputProps: { min: 2, max: 6 } }}
                    />
                  </Grid>
                )}
                
                {parametersForm.gameType === 'dictation' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Mode</InputLabel>
                      <Select
                        value={parametersForm.mode}
                        onChange={(e) => handleParametersChange('mode', e.target.value)}
                        label="Mode"
                      >
                        <MenuItem value="text">Texte</MenuItem>
                        <MenuItem value="word_lists">Liste de mots</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}
            
            <Grid item xs={12}>
              <Alert severity="info">
                La génération via le LLM peut prendre quelques secondes. 
                {parametersForm.useCustomPrompt 
                  ? 'Votre prompt personnalisé sera utilisé pour la génération.'
                  : 'L\'exercice sera généré avec les paramètres ci-dessus.'
                }
                {!parametersForm.useCustomPrompt && !parametersForm.competence && !parametersForm.theme && (
                  <Typography color="warning.main" sx={{ mt: 1 }}>
                    Pour de meilleurs résultats, sélectionnez une compétence ou un thème.
                  </Typography>
                )}
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleGenerate} 
            variant="contained" 
            color="primary"
            disabled={generating || !parametersForm.gameType || 
              (parametersForm.useCustomPrompt && !parametersForm.selectedPromptId && !parametersForm.customPrompt)}
            startIcon={generating ? <CircularProgress size={20} /> : <FlashOn />}
          >
            {generating ? 'Génération...' : 'Générer'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Dialog pour créer un nouveau prompt
  const renderPromptDialog = () => {
    const examples = getPromptExamples(parametersForm.gameType);
    
    return (
      <Dialog open={showPromptDialog} onClose={() => setShowPromptDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Créer un nouveau prompt - {getGameConfig(parametersForm.gameType)?.label}
            <Button
              size="small"
              onClick={() => setShowPromptExamples(!showPromptExamples)}
              endIcon={showPromptExamples ? <ExpandLess /> : <ExpandMore />}
            >
              Voir des exemples
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Exemples de prompts */}
            <Collapse in={showPromptExamples}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    💡 Exemples de prompts pour {getGameConfig(parametersForm.gameType)?.label}
                  </Typography>
                  {(examples || []).map((example, index) => (
                    <Accordion key={index} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2">{example.name}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {example.prompt}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<ContentCopy />}
                          onClick={() => setNewPrompt(prev => ({ ...prev, promptContent: example.prompt }))}
                          sx={{ mt: 1 }}
                        >
                          Utiliser cet exemple
                        </Button>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Paper>
              </Grid>
            </Collapse>

            {/* Informations du prompt */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                📝 Informations du prompt
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom du prompt"
                value={newPrompt.name}
                onChange={(e) => setNewPrompt(prev => ({ ...prev, name: e.target.value }))}
                placeholder="ex: Génération de mots de vocabulaire"
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optionnel)"
                value={newPrompt.description}
                onChange={(e) => setNewPrompt(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description du prompt pour faciliter sa réutilisation"
              />
            </Grid>
            
            {/* Instructions de génération */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                🎯 Instructions de génération
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Concentrez-vous uniquement sur les instructions pédagogiques. 
                Le format de sortie sera automatiquement ajouté.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Instructions de génération"
                value={newPrompt.promptContent}
                onChange={(e) => setNewPrompt(prev => ({ ...prev, promptContent: e.target.value }))}
                placeholder="Décrivez ce que le LLM doit générer. Utilisez {{variable}} pour les paramètres dynamiques."
                helperText="Exemple: Génère {{totalWords}} mots de vocabulaire sur le thème {{theme}} pour le niveau {{schoolLevel}}."
                required
              />
            </Grid>

            {/* Format attendu (lecture seule) */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                📋 Format de sortie (automatique)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Ce format sera automatiquement ajouté à vos instructions lors de la génération.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              {loadingFormatInstructions ? (
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography>Chargement du format...</Typography>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={12}
                  label="Format de sortie JSON"
                  value={formatInstructions}
                  InputProps={{
                    readOnly: true,
                    style: { 
                      backgroundColor: '#f5f5f5',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                  helperText="Ce format est généré automatiquement selon le type de jeu sélectionné"
                />
              )}
            </Grid>

            {/* Conseils */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>✨ Conseils pour de meilleurs résultats :</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  • Soyez spécifique sur le contenu pédagogique souhaité<br/>
                  • Utilisez les variables disponibles: {'{'}schoolLevel{'}'}, {'{'}difficulty{'}'}, {'{'}theme{'}'}, etc.<br/>
                  • Précisez le type d'erreurs ou de difficultés à inclure<br/>
                  • Le format JSON sera automatiquement ajouté - concentrez-vous sur le contenu !
                </Typography>
              </Alert>
            </Grid>

            {/* Conseils spécifiques par type de jeu */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
                <Typography variant="subtitle2" gutterBottom color="warning.main">
                  ⚠️ Conseils pour {getGameConfig(parametersForm.gameType)?.label}
                </Typography>
                {parametersForm.gameType === 'speed_game' && (
                  <Typography variant="body2">
                    • Spécifiez le type d'erreurs à générer (orthographe, conjugaison, etc.)<br/>
                    • Demandez des phrases d'usage pour contextualiser<br/>
                    • Précisez le niveau de difficulté des erreurs
                  </Typography>
                )}
                {parametersForm.gameType === 'text_holes' && (
                  <Typography variant="body2">
                    • Demandez un texte cohérent et engageant<br/>
                    • Spécifiez sur quoi doivent porter les trous (accords, vocabulaire, etc.)<br/>
                    • Limitez le nombre de phrases (3-5 max)
                  </Typography>
                )}
                {parametersForm.gameType === 'correction_game' && (
                  <Typography variant="body2">
                    • Demandez des erreurs réalistes et pédagogiques<br/>
                    • Spécifiez le type d'erreurs (orthographe, grammaire, etc.)<br/>
                    • Demandez des explications claires pour chaque erreur
                  </Typography>
                )}
                {parametersForm.gameType === 'word_lists' && (
                  <Typography variant="body2">
                    • Spécifiez le thème et le niveau de vocabulaire<br/>
                    • Demandez une variété de types de mots<br/>
                    • Évitez les mots trop complexes pour le niveau
                  </Typography>
                )}
                {parametersForm.gameType === 'dictation' && (
                  <Typography variant="body2">
                    • Demandez un texte narratif court et engageant<br/>
                    • Spécifiez les difficultés orthographiques à inclure<br/>
                    • Limitez la longueur selon le niveau
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Annuler</Button>
          <Button 
            onClick={handleSavePrompt}
            variant="contained"
            disabled={!newPrompt.name || !newPrompt.promptContent}
          >
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Fonction pour sauvegarder un nouveau prompt
  const handleSavePrompt = async () => {
    if (!newPrompt.name || !newPrompt.promptContent) {
      setErrorMessage('Le nom et le contenu du prompt sont requis');
      return;
    }

    try {
      const promptData = {
        name: newPrompt.name,
        description: newPrompt.description,
        gameType: parametersForm.gameType,
        promptContent: newPrompt.promptContent
        // Les instructions de format sont générées automatiquement par le backend
      };

      const savedPrompt = await AdminService.createPrompt(promptData);
      
      // Ajouter le nouveau prompt à la liste et le sélectionner
      setAvailablePrompts(prev => [...prev, savedPrompt]);
      setParametersForm(prev => ({
        ...prev,
        selectedPromptId: savedPrompt.id,
        useCustomPrompt: true
      }));
      
      // Réinitialiser le formulaire et fermer le dialog
      setNewPrompt({ name: '', description: '', promptContent: '' });
      setFormatInstructions('');
      setShowPromptDialog(false);
      setShowPromptExamples(false);
      setErrorMessage('');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du prompt:', error);
      setErrorMessage('Erreur lors de la sauvegarde du prompt');
    }
      };

  if (loading) {
    return <AdminLayout><LoadingScreen /></AdminLayout>;
  }

  const exerciseDisplayLabel = exercise.title?.trim() || exercise.id || id;

  return (
    <AdminLayout>
      <Box sx={{ p: 0 }}>
        {/* Barre d'actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <Button 
              startIcon={<ArrowBack />} 
              onClick={() => navigate(ROUTES.ADMIN.EXERCISES.LIST)}
              sx={{ mr: 2 }}
            >
              Retour
            </Button>
            <Box>
              <Typography variant="h4">
                {isNewMode
                  ? 'Nouvel exercice'
                  : isViewMode
                    ? `Détails de l'exercice: ${exerciseDisplayLabel}`
                    : `Modifier l'exercice: ${exerciseDisplayLabel}`}
              </Typography>
              {/* Notion de l'exercice CHARGÉ. Le sélecteur « Compétence » plus bas
                  appartient au formulaire de GÉNÉRATION et ne reflète pas
                  l'exercice ouvert : sans ce bandeau, on relit sans savoir à quoi
                  l'exercice se rattache. Les libellés viennent du référentiel
                  (jointure API), l'exercice ne portant plus que `notion_id`. */}
              {!isNewMode && (
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  {exercise?.notionLabel ? (
                    <>
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={exercise.notionLabel}
                        title={exercise.notionId}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {exercise.domainLabel} · {exercise.competenceLabel}
                      </Typography>
                    </>
                  ) : (
                    <Chip size="small" color="warning" variant="outlined" label="sans notion" />
                  )}
                </Box>
              )}
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {/* Navigation précédent/suivant */}
            {!isNewMode && voisins && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<NavigateBefore />}
                  onClick={goToPreviousExercise}
                  disabled={!voisins.precedent}
                  size="small"
                >
                  Précédent
                </Button>
                <Tooltip
                  title={voisins.dansLaListe
                    ? 'Rang dans la liste filtrée et triée'
                    : 'Cet exercice ne passe plus les filtres de la liste (statut changé ?) : précédent et suivant sont ceux qui l\'entouraient.'}
                >
                  <Typography variant="body2" color="textSecondary" sx={{ mx: 1, whiteSpace: 'nowrap' }}>
                    {voisins.dansLaListe ? `${voisins.rang} / ${voisins.total}` : `hors liste · ${voisins.total}`}
                  </Typography>
                </Tooltip>
                <Button
                  variant="outlined"
                  endIcon={<NavigateNext />}
                  onClick={goToNextExercise}
                  disabled={!voisins.suivant}
                  size="small"
                >
                  Suivant
                </Button>
                {!voisins.dansLaListe && (
                  <Tooltip title="Remettre les filtres de la liste à zéro et naviguer dans tous les exercices">
                    <Chip
                      label="Filtres appliqués"
                      color="warning"
                      size="small"
                      onClick={resetFiltersAndReload}
                      sx={{ ml: 1, cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </>
            )}

            {!isNewMode && !isViewMode && (
              <Chip
                size="small"
                sx={{ ml: 1 }}
                label={
                  autoSaveState === 'saving'
                    ? 'Enregistrement…'
                    : autoSaveState === 'saved'
                      ? 'Enregistré'
                      : autoSaveState === 'error'
                        ? 'Erreur de sauvegarde'
                        : 'Sauvegarde auto'
                }
                color={
                  autoSaveState === 'error'
                    ? 'error'
                    : autoSaveState === 'saved'
                      ? 'success'
                      : 'default'
                }
                variant={autoSaveState === 'idle' ? 'outlined' : 'filled'}
              />
            )}
            {!isViewMode && (
              <>
                <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<FlashOn />}
                  onClick={() => setGenerateDialog(true)}
                  sx={{ ml: 2 }}
                >
                  Générer du contenu
                </Button>
                {isNewMode && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ ml: 1 }}
                  >
                    {saving ? 'Sauvegarde...' : 'Créer l\'exercice'}
                  </Button>
                )}
              </>
            )}
          </Box>
          
          {isViewMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/admin/exercises/edit/${id}`)}
            >
              Modifier
            </Button>
          )}
          
        </Box>
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Configuration" />
          <Tab label="Paramètres" />
          <Tab label="Contenu" />
          {!isNewMode && <Tab label="Statistiques" />}
        </Tabs>

        {/* Tab 1: Configuration (type, source — définis à la création) */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type de jeu</InputLabel>
                  <Select
                    value={exercise.gameType}
                    onChange={(e) => handleChange('gameType', e.target.value)}
                    label="Type de jeu"
                    disabled={!isNewMode || isViewMode}
                  >
                    {Object.values(GAME_TYPES || {}).map((game) => (
                      <MenuItem key={game.id} value={game.id}>
                        {game.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={exercise.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    label="Type"
                    disabled={!isNewMode || isViewMode}
                  >
                    <MenuItem value="manual">Manuel</MenuItem>
                    <MenuItem value="llm">LLM</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select
                    value={exercise.source}
                    onChange={(e) => handleChange('source', e.target.value)}
                    label="Source"
                    disabled={!isNewMode || isViewMode}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="llm">LLM</MenuItem>
                    <MenuItem value="import">Import</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tab 2: Paramètres */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Paramètres de l'exercice</Typography>
              {!isViewMode && (
                <Button 
                  variant="outlined" 
                  color="primary"
                  startIcon={<ContentCopy />}
                  onClick={() => navigator.clipboard.writeText(JSON.stringify(exercise.parameters, null, 2))}
                >
                  Copier
                </Button>
              )}
            </Box>
            <JSONEditor
              value={exercise.parameters}
              onChange={handleParametersJsonChange}
              readOnly={isViewMode}
              height="400px"
            />
          </Paper>
        )}

        {/* Tab 3: Contenu */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            {/* Encart revue : infos générales + statuts */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: 'grey.50',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Revue & informations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Mettez à jour le titre, les statuts et marquez l&apos;exercice comme validé une fois la relecture terminée.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Titre"
                    value={exercise.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    disabled={isViewMode}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={exercise.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    disabled={isViewMode}
                    multiline
                    minRows={1}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={exercise.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        disabled={isViewMode}
                        color="success"
                      />
                    }
                    label={exercise.isActive ? 'Actif' : 'Inactif'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!exercise.isFlagged}
                        onChange={(e) => handleChange('isFlagged', e.target.checked)}
                        disabled={isViewMode}
                        color="warning"
                      />
                    }
                    label="Signalé"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  {isNewMode || isViewMode ? (
                    <Chip
                      label={VALIDATION_STATUS_LABELS[exercise.validationStatus] || 'Nouveau'}
                      color={VALIDATION_STATUS_COLORS[exercise.validationStatus] || 'default'}
                      size="small"
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>Validation</InputLabel>
                      <Select
                        value={exercise.validationStatus || VALIDATION_STATUS.NOUVEAU}
                        onChange={(e) => handleChange('validationStatus', e.target.value)}
                        label="Validation"
                      >
                        <MenuItem value={VALIDATION_STATUS.NOUVEAU}>
                          {VALIDATION_STATUS_LABELS[VALIDATION_STATUS.NOUVEAU]}
                        </MenuItem>
                        <MenuItem value={VALIDATION_STATUS.EN_ATTENTE}>
                          {VALIDATION_STATUS_LABELS[VALIDATION_STATUS.EN_ATTENTE]}
                        </MenuItem>
                        <MenuItem value={VALIDATION_STATUS.VALIDE}>
                          {VALIDATION_STATUS_LABELS[VALIDATION_STATUS.VALIDE]}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              </Grid>
              <ExerciseParametersSummaryForm
                parameters={exercise.parameters || {}}
                gameType={exercise.gameType}
                readOnly={isViewMode}
                onChange={(next) => handleChange('parameters', next)}
                notionId={exercise.notionId ?? null}
                onNotionChange={isNewMode ? undefined : (id) => handleChange('notionId', id)}
              />
            </Paper>

            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Tabs
                value={0}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Éditeur JSON" />
                <Tab label="Aperçu" disabled={!exercise.content || Object.keys(exercise.content).length === 0} />
              </Tabs>
              {!isViewMode && exercise.gameType && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AutoFixHigh />}
                  onClick={handleCorrectWithLLM}
                  disabled={!exercise.content || Object.keys(exercise.content || {}).length === 0}
                >
                  Corriger
                </Button>
              )}
            </Box>

            {(contentValidation || estimatedLevel) && (
              <Box sx={{ mb: 2 }}>
                {estimatedLevel && (() => {
                  const declaredCycle = exercise.parameters?.cycle;
                  const declaredDiff = exercise.parameters?.difficulty;
                  const cycleMismatch = declaredCycle != null && estimatedLevel.cycle != null && Number(declaredCycle) !== Number(estimatedLevel.cycle);
                  const diffMismatch = declaredDiff && estimatedLevel.difficulty && declaredDiff !== estimatedLevel.difficulty;
                  return (
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">Niveau estimé (IA) :</Typography>
                      <Chip
                        size="small"
                        label={`Cycle ${estimatedLevel.cycle ?? '?'} · ${estimatedLevel.difficulty ?? '?'}`}
                        color={cycleMismatch || diffMismatch ? 'warning' : 'success'}
                      />
                      {(cycleMismatch || diffMismatch) && (
                        <Typography variant="caption" color="warning.main">
                          ≠ déclaré (cycle {declaredCycle ?? '?'} · {declaredDiff ?? '?'})
                        </Typography>
                      )}
                      {estimatedLevel.rationale && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          — {estimatedLevel.rationale}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
                {contentValidation && <ExerciseValidationPanel validation={contentValidation} />}
              </Box>
            )}

            {exercise.gameType && (
              <ExerciseContentVisualEditor
                gameType={exercise.gameType}
                content={exercise.content}
                onChange={handleContentChange}
                readOnly={isViewMode}
                onCorrectItem={!isViewMode ? handleCorrectItemWithLLM : undefined}
                correctingItemKey={correctingItemKey}
              />
            )}
            
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Éditeur JSON (avancé)
            </Typography>
            <Box mb={3}>
              <JSONEditor
                value={exercise.content}
                onChange={handleContentChange}
                readOnly={isViewMode}
                height="400px"
              />
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Typography variant="h6" gutterBottom>Aperçu</Typography>
            <Box mb={3}>
              {exercise.gameType && exercise.content ? (
                <ExercisePreview 
                  gameType={exercise.gameType} 
                  content={exercise.content} 
                />
              ) : (
                <Typography color="textSecondary">
                  Sélectionnez un type de jeu et ajoutez du contenu pour voir l'aperçu
                </Typography>
              )}
            </Box>
            
          </Paper>
        )}

       
        {/* Tab 4: Statistiques */}
        {activeTab === 3 && !isNewMode && (
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">Statistiques d'utilisation</Typography>
              <Button 
                startIcon={<Refresh />} 
                variant="outlined"
                onClick={refreshStats}
              >
                Actualiser
              </Button>
            </Box>
            
            {usageStats && usageStats.reponses > 0 ? (
              <Grid container spacing={3}>
                {[
                  ['Parties', usageStats.parties],
                  ['Joueurs', usageStats.joueurs],
                  ['Taux d\'échec', usageStats.tauxEchec != null
                    ? `${Math.round(usageStats.tauxEchec * 100)} %${usageStats.parties < usageStats.seuilParties ? ' *' : ''}`
                    : '—'],
                  ['Questions', usageStats.questions],
                  ['Passées', usageStats.passees],
                  ['Temps moyen', usageStats.tempsMoyenMs != null
                    ? `${(usageStats.tempsMoyenMs / 1000).toFixed(1)} s` : '—'],
                ].map(([libelle, valeur]) => (
                  <Grid item xs={6} md={2} key={libelle}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h5">{valeur ?? 0}</Typography>
                      <Typography variant="body2" color="textSecondary">{libelle}</Typography>
                    </Paper>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Échec = première réponse fausse ou passée, par question et par partie
                    ({usageStats.echecs} sur {usageStats.questions} ; {usageStats.reponses} réponses brutes au journal).
                    {usageStats.parties < usageStats.seuilParties
                      && ` * Moins de ${usageStats.seuilParties} parties : la liste n'affiche pas encore ce taux.`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Première réponse : {usageStats.premiere ? new Date(usageStats.premiere).toLocaleString('fr-FR') : '—'}
                    {' · '}
                    Dernière : {usageStats.derniere ? new Date(usageStats.derniere).toLocaleString('fr-FR') : '—'}
                  </Typography>
                </Grid>

                {usageStats.recentUsage?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Dernières réponses</Typography>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Joueur</TableCell>
                            <TableCell>Résultat</TableCell>
                            <TableCell>Réponse</TableCell>
                            <TableCell>Attendu</TableCell>
                            <TableCell>Temps</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {usageStats.recentUsage.map((usage) => (
                            <TableRow key={usage.id}>
                              <TableCell title={usage.userId}>{usage.displayName || usage.userId}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={usage.skipped ? 'Passée' : usage.isCorrect ? 'Juste' : 'Fausse'}
                                  color={usage.skipped ? 'default' : usage.isCorrect ? 'success' : 'error'}
                                />
                              </TableCell>
                              <TableCell>{usage.userAnswer ?? '—'}</TableCell>
                              <TableCell>{usage.expectedAnswer ?? '—'}</TableCell>
                              <TableCell>
                                {usage.responseTimeMs != null ? `${(usage.responseTimeMs / 1000).toFixed(1)} s` : '—'}
                              </TableCell>
                              <TableCell>{new Date(usage.occurredAt).toLocaleString('fr-FR')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography color="textSecondary">
                Aucune réponse enregistrée pour cet exercice (le journal des réponses commence le 28 août 2026).
              </Typography>
            )}
          </Paper>
        )}

        {renderGenerateDialog()}
        {renderPromptDialog()}

        {/* ✨ NOUVEAU DIALOGUE POUR SAUVEGARDER UN PROMPT TESTÉ */}
        <Dialog 
          open={showPromptEditor} 
          onClose={() => setShowPromptEditor(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            Sauvegarder ce prompt
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom du prompt"
                    value={newPrompt.name}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="ex: Speed Game - Niveau CE2"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Version/Description"
                    value={newPrompt.description}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="ex: Version optimisée pour les verbes"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    label="Contenu du prompt"
                    value={newPrompt.promptContent || currentPromptVersion}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, promptContent: e.target.value }))}
                    helperText="Ce prompt sera sauvegardé et pourra être réutilisé"
                  />
                </Grid>
                
                {/* Aperçu des paramètres utilisés */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Paramètres utilisés pour ce prompt :
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label={`Type: ${parametersForm.gameType}`} size="small" />
                      <Chip label={`Niveau: ${parametersForm.schoolLevel}`} size="small" />
                      <Chip label={`Difficulté: ${parametersForm.difficulty}`} size="small" />
                      {parametersForm.theme && (
                        <Chip label={`Thème: ${parametersForm.theme}`} size="small" />
                      )}
                      {parametersForm.competence && (
                        <Chip label={`Compétence: ${parametersForm.competence}`} size="small" />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPromptEditor(false)}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await savePromptVersion(
                    newPrompt.promptContent || currentPromptVersion,
                    newPrompt.name,
                    newPrompt.description
                  );
                  setShowPromptEditor(false);
                  setNewPrompt({ name: '', description: '', promptContent: '' });
                  // Afficher un message de succès
                  setErrorMessage('');
                } catch (error) {
                  setErrorMessage('Erreur lors de la sauvegarde du prompt');
                }
              }}
              disabled={!newPrompt.name || (!newPrompt.promptContent && !currentPromptVersion)}
            >
              Sauvegarder
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de correction LLM */}
        <Dialog
          open={llmCorrectionDialog.open}
          onClose={() => !llmCorrectionDialog.loading && setLlmCorrectionDialog(prev => ({ ...prev, open: false }))}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHigh color="secondary" />
            Correction de l'exercice via LLM
          </DialogTitle>
          <DialogContent>
            {llmCorrectionDialog.loading ? (
              <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
                <CircularProgress />
                <Typography color="text.secondary">
                  Le LLM analyse et corrige l'exercice...
                </Typography>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Vérifiez et modifiez si besoin la réponse du LLM avant de l'appliquer à l'exercice.
                  {llmCorrectionDialog.batchInfo && (
                    <> Correction effectuée en {llmCorrectionDialog.batchInfo.chunkCount} lots parallèles de 20 éléments max.</>
                  )}
                </Alert>
                {llmCorrectionDialog.parseError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {llmCorrectionDialog.parseError}
                  </Alert>
                )}
                {llmCorrectionDialog.issues?.length > 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Pourquoi cette correction ({llmCorrectionDialog.issues.length})
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {llmCorrectionDialog.issues.map((issue, i) => (
                        <li key={i}>
                          <Typography variant="body2">
                            <strong>[{issue.type}{issue.severity ? ` · ${issue.severity}` : ''}]</strong>{' '}
                            {issue.description}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Aucun défaut détecté — le LLM n'a signalé aucune correction nécessaire.
                  </Alert>
                )}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Contenu corrigé (JSON éditable)
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={12}
                  maxRows={24}
                  value={llmCorrectionDialog.editedContent}
                  onChange={(e) => setLlmCorrectionDialog(prev => ({ ...prev, editedContent: e.target.value, parseError: null }))}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                  variant="outlined"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setLlmCorrectionDialog(prev => ({ ...prev, open: false }))}
              disabled={llmCorrectionDialog.loading}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleApplyLLMCorrection}
              disabled={llmCorrectionDialog.loading || !llmCorrectionDialog.editedContent}
              startIcon={<Save />}
            >
              Appliquer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default ExerciseDetailScreen; 